import React from "react";
import { useTranslation } from "react-i18next";
import {
  Identifiable,
  OrganizationalUnit,
  allOrganizationalUnitsFilter,
} from "@planzone/dto";
import {
  useRetrieveAllOrganizationalUnits,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";
import { TextInput } from "@common/textInput";
import { Loader } from "@common/loader";
import { Flag, InfiniteScroll } from "@common/infiniteScroll";
import { Row } from "./row";
import { Props } from "./types";
import styles from "./styles.scss";

const ROOT_KEY = "";
const SEARCH_DELAY = 300;

// The units come in tree order, parents first, so every loaded page can be indented under its parents.
function OrganizationalUnitsHierarchy({
  excludedUuid,
  children,
  isSelected,
  select,
}: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    organizationalUnits,
    isRequestPending,
    filter,
    totalPages,
    retrieveAllOrganizationalUnits,
  } = useRetrieveAllOrganizationalUnits();
  const [search, setSearch] = React.useState("");
  const childrenByParent = React.useMemo(() => {
    const map = new Map<string, Identifiable<OrganizationalUnit>[]>();

    organizationalUnits.forEach((organizationalUnit) => {
      const key = organizationalUnit.parentUuid ?? ROOT_KEY;
      map.set(key, [...(map.get(key) ?? []), organizationalUnit]);
    });

    return map;
  }, [organizationalUnits]);

  React.useEffect(() => {
    const timeout = window.setTimeout(
      () =>
        retrieveAllOrganizationalUnits({
          planzone,
          filter: {
            ...allOrganizationalUnitsFilter,
            name: search,
            excludedUuid,
          },
        }),
      SEARCH_DELAY,
    );

    return () => window.clearTimeout(timeout);
  }, [search, excludedUuid]);

  return (
    <div className={styles.organizationalUnitsHierarchy}>
      <div className={styles.search}>
        <TextInput
          placeholder={t("common.filterMenu.search")}
          value={search}
          input={setSearch}
          autoFocus
        />
      </div>
      <div className={styles.scrollPane}>
        <InfiniteScroll canFetchOnIdle={!isRequestPending}>
          {() => (
            <div>
              {children}
              {(childrenByParent.get(ROOT_KEY) ?? []).map(
                (organizationalUnit) => (
                  <Row
                    key={organizationalUnit.uuid}
                    organizationalUnit={organizationalUnit}
                    childrenByParent={childrenByParent}
                    isSelected={isSelected}
                    select={select}
                  />
                ),
              )}
              {isRequestPending && (
                <div className={styles.loader}>
                  <Loader />
                </div>
              )}
              <Flag
                name="organizationalUnitsHierarchyFlag"
                retrieveItems={() =>
                  !isRequestPending &&
                  retrieveAllOrganizationalUnits({ planzone, filter })
                }
                isDisabled={
                  isRequestPending ||
                  filter.page === 0 ||
                  filter.page >= totalPages
                }
              />
            </div>
          )}
        </InfiniteScroll>
      </div>
    </div>
  );
}

export { OrganizationalUnitsHierarchy };
